import { Formik } from 'formik';
import * as yup from 'yup';
import { FormGroup } from 'reactstrap';
import { gql, useQuery } from '@apollo/client';
import useMutateWithState from '../../hooks/useMutateWithState';
import {
  DeleteButton,
  Message,
  SubmitButton,
  TemplateForm,
  ValidatedInput,
} from '../templates/template-components';
import UploadTeamImageButton from './UploadTeamImageButton';
import { getFirstQueryResult } from '../../utils/graphql';

const teamSchema = yup.object().shape({
  name: yup.string().required('Required'),
});

const TeamSignedWriteUrlQuery = gql`
  query TeamSignedWriteUrl(
    $id: ID!
    $delete: Boolean
    $imageExtension: String
  ) {
    teamSignedWriteUrl(
      id: $id
      delete: $delete
      imageExtension: $imageExtension
    )
  }
`;

export const writeImageToS3 = async (team, getSignedWriteUrl) => {
  // Only send request to S3 if deleting or updating the image
  if (!team.deleteImage && !team.file) return Promise.resolve();

  const { deleteImage, file, id, imageExtension } = team;
  // Get pre-signed URL
  const { data } = await getSignedWriteUrl({
    delete: deleteImage,
    id,
    imageExtension,
  });

  // Send request to S3
  const requestOptions = deleteImage
    ? { method: 'DELETE' }
    : {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': 'application/octet-stream' },
      };
  const response = await fetch(getFirstQueryResult(data), requestOptions);
  if (!response.ok) {
    throw new Error(
      `Cannot ${deleteImage ? 'delete' : 'upload'} image: ${
        response.statusText
      }`,
    );
  }
  return Promise.resolve();
};

const TeamForm = ({
  resource,
  msg,
  successMessage,
  submitMutation,
  submitButtonText,
  showDelete,
  deleteMutation,
  showLabels = true,
}) => {
  const [{ loading }, mutateAndSetState] = useMutateWithState();
  const { refetch: refetchSignedPutUrl } = useQuery(TeamSignedWriteUrlQuery, {
    skip: true,
  });

  const { id, deleteImage, file, hasImage, imageExtension, name, updatedAt } =
    resource || {};

  const initialValues = resource
    ? { id, deleteImage, file, hasImage, imageExtension, name, updatedAt }
    : { name: '' };

  const onSubmit = async (values) => {
    mutateAndSetState(
      () =>
        writeImageToS3(values, refetchSignedPutUrl).then(() =>
          submitMutation({
            variables: {
              input: {
                ...values,
                deleteImage: undefined,
                file: undefined,
              },
            },
          }),
        ),
      successMessage ?? 'Team updated!',
    );
  };

  if (msg) return <Message>{msg}</Message>;

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={teamSchema}
      onSubmit={onSubmit}
      enableReinitialize
    >
      {({ isSubmitting }) => (
        <TemplateForm>
          <ValidatedInput
            type="text"
            name="name"
            label={showLabels ? 'Name' : undefined}
            placeholder="Team name"
          />
          {initialValues.id && (
            <FormGroup>
              <UploadTeamImageButton imageUrl={resource.signedUrl} />
            </FormGroup>
          )}
          <SubmitButton
            loading={loading}
            disabled={isSubmitting || loading}
            className="mr-2"
          >
            {submitButtonText}
          </SubmitButton>
          {showDelete && (
            <DeleteButton
              onDelete={() =>
                mutateAndSetState(() =>
                  deleteMutation({
                    variables: {
                      input: {
                        id: resource?.id,
                        updatedAt: resource?.updatedAt,
                      },
                    },
                  }),
                )
              }
              disabled={isSubmitting || loading}
            >
              Delete
            </DeleteButton>
          )}
        </TemplateForm>
      )}
    </Formik>
  );
};

export default TeamForm;
